import { MONEY_STRING_PATTERN, type MoneyString } from "./admin-usage";

export const ADMIN_USER_ROLES = ["admin", "dealer", "user"] as const;
export type AdminUserRole = (typeof ADMIN_USER_ROLES)[number];

export const ADMIN_USER_STATUSES = ["active", "banned"] as const;
export type AdminUserStatus = (typeof ADMIN_USER_STATUSES)[number];

export interface AdminUserListQuery {
  /** Matches email, name, or exact user id. */
  query?: string;
  role?: AdminUserRole;
  status?: AdminUserStatus;
  walletStatus?: "active" | "frozen" | "closed";
  /** Page size, 1..200. */
  limit: number;
  cursor?: string;
}

export interface AdminUserWalletSummary {
  currency: string;
  balance: MoneyString;
  reservedBalance: MoneyString;
  status: string;
}

export interface AdminUserSummaryRow {
  userId: string;
  email: string;
  name: string;
  emailVerified: boolean;
  role: string;
  status: string;
  createdAt: string;
  /** Non-revoked, unexpired API keys. */
  keyCount: number;
  wallets: AdminUserWalletSummary[];
  lastKeyUsedAt: string | null;
  /** Successfully settled lifetime amounts by currency; refunded charges are excluded. */
  settledConsumptionByCurrency?: Record<string, MoneyString>;
  usageLast30Days: {
    /** Backend snapshot window; absent only on older API versions. */
    windowStart?: string;
    windowEnd?: string;
    requests: number;
    /** Settled sale amounts per currency over the same 30 days. */
    saleByCurrency: Record<string, MoneyString>;
  };
}

export interface AdminUserKeyRow {
  id: string;
  name: string;
  prefix: string;
  lastFour: string;
  /** Derived: revoked > expired > active. */
  status: "active" | "revoked" | "expired";
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
}

export interface AdminUserDiscountRow {
  id: string;
  modelId: string;
  modelPublicId: string | null;
  operation: string;
  /** Basis points off the public sale price; 2000 = 20% off. */
  discountBps: number;
  status: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  note: string | null;
}

export interface AdminUserPricingAdjustmentRow {
  id: string;
  /** "user" rules apply to every key of the user; "api_key" rules win over them. */
  subjectType: "user" | "api_key";
  apiKeyId: string | null;
  keyName: string | null;
  keyHint: string | null;
  modelId: string | null;
  modelPublicId: string | null;
  operation: string | null;
  /** Positive decimal multiplier applied to the standard sale price; 1.0000 = unchanged. */
  multiplier: string;
  status: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  note: string | null;
}

export interface AdminUserDetail extends AdminUserSummaryRow {
  keys: AdminUserKeyRow[];
  discounts: AdminUserDiscountRow[];
  pricingAdjustments: AdminUserPricingAdjustmentRow[];
}

export interface AdminUserListPage {
  data: AdminUserSummaryRow[];
  /** Present when more users follow; opaque, valid only for the same query. */
  nextCursor: string | null;
}

export interface AdminUserListCursor {
  createdAt: string;
  userId: string;
}

export function validateAdminUserListQuery(
  value: unknown,
): asserts value is AdminUserListQuery {
  if (!isObject(value)) throw new Error("User list query must be an object");
  if (value.query !== undefined
    && (typeof value.query !== "string" || value.query.trim() === "" || value.query.length > 320)) {
    throw new Error("query must be a non-empty string of at most 320 characters");
  }
  if (value.role !== undefined
    && !(ADMIN_USER_ROLES as readonly string[]).includes(String(value.role))) {
    throw new Error("role is not supported");
  }
  if (value.status !== undefined
    && !(ADMIN_USER_STATUSES as readonly string[]).includes(String(value.status))) {
    throw new Error("status is not supported");
  }
  if (value.walletStatus !== undefined && !["active", "frozen", "closed"].includes(String(value.walletStatus))) {
    throw new Error("walletStatus is not supported");
  }
  if (!Number.isSafeInteger(value.limit) || (value.limit as number) < 1 || (value.limit as number) > 200) {
    throw new Error("limit must be an integer between 1 and 200");
  }
  if (value.cursor !== undefined && (typeof value.cursor !== "string" || value.cursor === "")) {
    throw new Error("cursor must be a non-empty string");
  }
}

const USER_CURSOR_PREFIX = "v1.user.";

export function encodeAdminUserCursor(cursor: AdminUserListCursor): string {
  return `${USER_CURSOR_PREFIX}${encodeURIComponent(cursor.createdAt)}|${encodeURIComponent(cursor.userId)}`;
}

export function parseAdminUserCursor(value: string): AdminUserListCursor | null {
  try {
    if (!value.startsWith(USER_CURSOR_PREFIX)) return null;
    const parts = value.slice(USER_CURSOR_PREFIX.length).split("|");
    if (parts.length !== 2) return null;
    const [encodedCreatedAt, encodedUserId] = parts;
    if (!encodedCreatedAt || !encodedUserId) return null;
    const createdAt = decodeURIComponent(encodedCreatedAt);
    const userId = decodeURIComponent(encodedUserId);
    return Number.isFinite(Date.parse(createdAt)) && userId !== "" ? { createdAt, userId } : null;
  } catch {
    return null;
  }
}

export const ADMIN_USER_ACTIVITY_KINDS = ["ledger", "recharge", "hold", "audit"] as const;
export type AdminUserActivityKind = (typeof ADMIN_USER_ACTIVITY_KINDS)[number];

export interface AdminUserActivityRow {
  id: string;
  kind: AdminUserActivityKind;
  occurredAt: string;
  action: string;
  currency: string | null;
  amount: MoneyString | null;
  balanceAfter: MoneyString | null;
  status: string | null;
  referenceId: string | null;
  actorUserId: string | null;
  reason: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface AdminUserActivityPage {
  data: AdminUserActivityRow[];
  nextCursor: string | null;
}

export interface AdminUserWalletOpenInput {
  currency: "CNY" | "USD";
  reason: string;
}

export interface AdminUserWalletOpenResult {
  walletId: string;
  currency: string;
  created: boolean;
}

export function isMoneyString(value: string): boolean {
  return MONEY_STRING_PATTERN.test(value);
}

// ---- User permission operations (TODO-10) ----

export interface AdminUserUpdate {
  role?: AdminUserRole;
  status?: AdminUserStatus;
  /** Required; stored verbatim on the admin audit event. */
  reason: string;
}

export interface AdminUserUpdateResult {
  userId: string;
  role: string;
  status: string;
  updatedAt: string;
}

export interface AdminUserDeleteInput {
  reason: string;
}

export interface AdminUserDeleteResult {
  userId: string;
  deleted: true;
}

export function validateAdminUserDelete(value: unknown): asserts value is AdminUserDeleteInput {
  if (!isObject(value)) throw new Error("User deletion must be an object");
  if (typeof value.reason !== "string" || value.reason.trim() === "" || value.reason.length > 500) {
    throw new Error("reason is required (at most 500 characters)");
  }
}

export function validateAdminUserUpdate(
  value: unknown,
): asserts value is AdminUserUpdate {
  if (!isObject(value)) throw new Error("User update must be an object");
  if (typeof value.reason !== "string" || value.reason.trim() === "" || value.reason.length > 500) {
    throw new Error("reason is required (at most 500 characters)");
  }
  if (value.role !== undefined
    && !(ADMIN_USER_ROLES as readonly string[]).includes(String(value.role))) {
    throw new Error("role is not supported");
  }
  if (value.status !== undefined
    && !(ADMIN_USER_STATUSES as readonly string[]).includes(String(value.status))) {
    throw new Error("status is not supported");
  }
  if (value.role === undefined && value.status === undefined) {
    throw new Error("At least one of role or status is required");
  }
}

// ---- API key management operations (TODO-11) ----

export interface AdminUserApiKeyUpdate {
  revoked?: boolean;
  /** Required; stored verbatim on the admin audit event. */
  reason: string;
}

export interface AdminUserApiKeyUpdateResult {
  id: string;
  name: string;
  prefix: string;
  lastFour: string;
  status: "active" | "revoked" | "expired";
  revokedAt: string | null;
  updatedAt: string;
}

export function validateAdminUserApiKeyUpdate(
  value: unknown,
): asserts value is AdminUserApiKeyUpdate {
  if (!isObject(value)) throw new Error("API key update must be an object");
  if (typeof value.reason !== "string" || value.reason.trim() === "" || value.reason.length > 500) {
    throw new Error("reason is required (at most 500 characters)");
  }
  if (value.revoked !== undefined && typeof value.revoked !== "boolean") {
    throw new Error("revoked must be a boolean");
  }
  if (value.revoked === undefined) {
    throw new Error("revoked is required");
  }
}

// ---- Wallet manual recharge (TODO-12) ----

export interface AdminWalletRechargeInput {
  userId: string;
  /** Single user billing currency: CNY. */
  currency: string;
  /** Positive decimal amount string, at most 8 fraction digits. */
  amount: MoneyString;
  /** External order reference; unique per payment provider "admin". */
  externalOrderId: string;
  /** Required; stored verbatim on the admin audit event. */
  reason: string;
}

export interface AdminWalletRechargeResult {
  rechargeId: string;
  /** Empty when `created` is false (idempotent retry of a known order). */
  ledgerEntryId: string;
  walletId: string;
  currency: string;
  /** Wallet balance after this operation. */
  balance: MoneyString;
  /** False when the external order was already credited; nothing changed. */
  created: boolean;
}

export function validateAdminWalletRechargeInput(
  value: unknown,
): asserts value is AdminWalletRechargeInput {
  if (!isObject(value)) throw new Error("Wallet recharge must be an object");
  if (typeof value.userId !== "string" || value.userId === "") {
    throw new Error("userId is required");
  }
  if (value.currency !== "CNY") {
    throw new Error("currency must be CNY");
  }
  if (typeof value.amount !== "string"
    || !isMoneyString(value.amount)
    || value.amount.startsWith("-")
    || Number(value.amount) <= 0) {
    throw new Error("amount must be a positive decimal string");
  }
  // wallets.balance is numeric(20,8): at most 12 integer digits.
  if ((value.amount.split(".")[0]?.length ?? 0) > 12) {
    throw new Error("amount exceeds the 12-digit integer limit of the wallet balance");
  }
  // "admin-recharge:" + externalOrderId must fit the 255-char idempotency key.
  if (typeof value.externalOrderId !== "string"
    || value.externalOrderId.trim() === ""
    || value.externalOrderId.trim().length > 240) {
    throw new Error("externalOrderId is required (at most 240 characters)");
  }
  if (typeof value.reason !== "string" || value.reason.trim() === "" || value.reason.length > 500) {
    throw new Error("reason is required (at most 500 characters)");
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
